// src/features/mech/plastics/questions/PartDetailsQuestion.jsx
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PerlTextField from "../../../../shared/components/PerlTextField";
import PerlChip from "../../../../shared/components/PerlChip";
import PerlLabelMedium from "../../../../shared/components/PerlLabelMedium";
import PerlSelect from "../../../../shared/components/PerlSelect";

const PartDetailsQuestion = forwardRef(({ value, onChange }, ref) => {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    white: "#ffffff",
    textPrimary: "#F1F2F3",
    textSecondary: "#9097A1",
    surface: "#191A1B",
  };
  const brandColor =
    theme.custom?.perlColors?.brandColor || theme.palette?.primary?.main || "#ff6034";

  // Parse the value object or initialize with empty strings
  const detailsValue = value || {
    wall: "",
    regrindPct: "",
    avgWallThickness: "",
    burstingFactor: "",
    noOfPly: "",
    flute: "",
  };

  const WALL_OPTIONS = ["Single Wall", "Double Wall", "Triple Wall"];
  const FLUTE_OPTIONS = ["A", "B", "C", "E"];
  const [validationError, setValidationError] = useState("");
  const [validationAttempted, setValidationAttempted] = useState(false);

  // Validate wall selection - only allow predefined options
  const validate = () => {
    setValidationAttempted(true);
    if (!detailsValue.wall || detailsValue.wall.trim() === "") {
      setValidationError("Please select a wall type");
      return false;
    }
    
    const trimmedValue = detailsValue.wall.trim();
    const isValid = WALL_OPTIONS.some(option => option === trimmedValue);
    
    if (!isValid) {
      setValidationError("Please select a wall type from the options below");
      return false;
    }
    
    setValidationError("");
    return true;
  };

  // Check if all required fields are filled - use current value prop
  const isAllFieldsFilled = () => {
    // Get current value (will be updated on each render)
    const currentValue = value || {
      wall: "",
      regrindPct: "",
      avgWallThickness: "",
      burstingFactor: "",
      noOfPly: "",
      flute: "",
    };
    
    // Wall is required
    if (!currentValue.wall || currentValue.wall.trim() === "") return false;
    const trimmedWall = currentValue.wall.trim();
    if (!WALL_OPTIONS.some(option => option === trimmedWall)) return false;
    
    // ETC (avgWallThickness) is required
    if (!currentValue.avgWallThickness || currentValue.avgWallThickness.toString().trim() === "") return false;
    
    // Bursting Factor is required
    if (!currentValue.burstingFactor || currentValue.burstingFactor.toString().trim() === "") return false;
    
    // No of ply is required
    if (!currentValue.noOfPly || currentValue.noOfPly.toString().trim() === "") return false;
    
    return true;
  };

  // Expose validate method to parent
  useImperativeHandle(ref, () => ({
    validate,
    isValid: isAllFieldsFilled,
  }), [value]); // Add value as dependency to update when it changes

  const handleFieldChange = (field, newValue) => {
    const updatedValue = {
      ...detailsValue,
      [field]: newValue,
    };
    onChange?.(updatedValue);
    // Clear validation error when user makes a change
    if (validationError) {
      setValidationError("");
      setValidationAttempted(false);
    }
  };

  const handleWallSelect = (wallType) => {
    // Toggle selection: if already selected, deselect; otherwise select
    const newValue = detailsValue.wall === wallType ? "" : wallType;
    handleFieldChange("wall", newValue);
  };
  
  // Prevent typing in the text field - only allow chip selection
  const handleWallTextChange = (e) => {
    // Don't allow manual typing - only allow chip selection
    // This prevents custom entries
    e.preventDefault();
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* <PerlLabelMedium sx={{ mb: 1, display: "block" }}>Wall:</PerlLabelMedium> */}
        <PerlTextField
          label="Wall"
          placeholder="Select a wall type from below"
          value={detailsValue.wall || ""}
          onChange={handleWallTextChange}
          onKeyDown={(e) => {
            // Prevent typing - only allow chip selection
            if (e.key !== "Tab" && e.key !== "Escape") {
              e.preventDefault();
            }
          }}
          inputProps={{
            readOnly: true,
          }}
          error={Boolean(validationError)}
          helperText={validationError}
          fullWidth
        />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {WALL_OPTIONS.map((wallType) => (
            <Box key={wallType} sx={{ width: "auto", flexShrink: 0 }}>
              <PerlChip
                label={wallType}
                onClick={() => handleWallSelect(wallType)}
                selected={detailsValue.wall === wallType}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <PerlTextField
        label="ETC"
        type="number"
        value={detailsValue.avgWallThickness || ""}
        onChange={(e) => handleFieldChange("avgWallThickness", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="Bursting Factor"
        type="number"
        value={detailsValue.burstingFactor || ""}
        onChange={(e) => handleFieldChange("burstingFactor", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlTextField
        label="No of ply"
        type="number"
        value={detailsValue.noOfPly || ""}
        onChange={(e) => handleFieldChange("noOfPly", e.target.value)}
        InputProps={{
          sx: {
            bgcolor: alpha(perlColors.white, 0.02),
            color: perlColors.textPrimary,
            fontSize: "0.9rem",
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(perlColors.textPrimary, 0.12) },
            "&:hover fieldset": { borderColor: alpha(brandColor, 0.8) },
            "&.Mui-focused fieldset": {
              borderColor: brandColor,
              boxShadow: `0 0 0 2px ${brandColor}33`,
            },
          },
        }}
        InputLabelProps={{
          sx: {
            color: perlColors.textSecondary,
            fontSize: "0.85rem",
            letterSpacing: "0.3px",
            "&.Mui-focused": { color: perlColors.textPrimary },
          },
        }}
      />
      <PerlSelect
        label="Flute"
        value={detailsValue.flute || ""}
        onChange={(e) => handleFieldChange("flute", e.target.value)}
        placeholder="Select flute type"
        options={FLUTE_OPTIONS}
      />
    </Box>
  );
});

PartDetailsQuestion.displayName = "PartDetailsQuestion";

export default PartDetailsQuestion;

